import { useMutation } from '@apollo/client';
import { graphql } from '@generated/gql';
import { Refresh } from '@mui/icons-material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Alert } from '@ovComponents/2-component/alert/alert';
import { Button } from '@ovComponents/2-component/button/button';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import { NetWorthWorkflowContext } from '@ovComponents/4-module/netWorthWorkflow/netWorthWorkflowContext';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const RUN_WORKFLOW_SUB_STEP_ACTION = graphql(`
  mutation runWorkflowSubStepAction($input: RunWorkflowSubStepActionInput!) {
    runWorkflowSubStepAction(input: $input) {
      workflowCompletion {
        id
      }
    }
  }
`);

const NetWorthAutomaticLink = ({
  onNext,
  workflowCompletion,
  subStepId,
}: {
  onNext: () => void;
  workflowCompletion: any;
  subStepId: string;
}) => {
  const { t } = useTranslation('netWorth');
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useGlobalToast();

  const { netWorthData } = useContext(NetWorthWorkflowContext);
  const { workflowData } = useContext(WorkflowContext);

  const applicableData =
    Object.keys(netWorthData).length !== 0 ? netWorthData : workflowData;
  const notificationGroup =
    applicableData.netWorthData.netWorthLinkingNotificationGroup;

  const [runWorkflowSubStepAction] = useMutation(RUN_WORKFLOW_SUB_STEP_ACTION, {
    onCompleted: () => {
      let message = t(
        'addNetWorthAutomatically.netWorthAutomaticLink.syncMessage',
      );
      if (notificationGroup) {
        message = `${message} ${t('addNetWorthAutomatically.netWorthAutomaticLink.notificationMessage')}`;
      }
      showToast({
        message,
        severity: 'success',
      });
      setError(null);
      onNext();
    },
  });

  const attemptIntegration = useCallback(async () => {
    if (!workflowCompletion.id) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const variables = {
      input: {
        workflowCompletionId: workflowCompletion.id,
        subStepId,
        actionType: applicableData.netWorthData.netWorthLinkingActionType,
      },
      skipErrorHandler: true,
    };

    try {
      await runWorkflowSubStepAction({ variables });
      return;
    } catch (err) {
      setError(err as Error);
    }
    setIsLoading(false);
  }, [workflowCompletion, subStepId, runWorkflowSubStepAction]);

  useEffect(() => {
    attemptIntegration();
  }, [attemptIntegration]);

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
      gap={1}
      minHeight='300px'
    >
      <Typography variant='headingMedium'>
        {t('addNetWorthAutomatically.netWorthAutomaticLink.title')}
      </Typography>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='center'
        height='450px'
      >
        {error && (
          <Box mb={2}>
            <Alert severity='error'>{error.message}</Alert>
            <Box mt={2} display='flex' justifyContent='center'>
              <Button
                onClick={attemptIntegration}
                disabled={isLoading}
                label={t('shared:retry')}
                variant='text'
                leadingIcon={Refresh}
              />
            </Box>
          </Box>
        )}
        {isLoading && <CircularProgress />}
      </Box>
    </Box>
  );
};

export default NetWorthAutomaticLink;
